import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    useMediaQuery,
    useTheme,
    Chip,
    Avatar,
    Button,
    Typography,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { DialogueModal } from '../DialogueModal/DialogueModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IUserSummary } from '../../types/global/helper';
import { IHost, IPartner } from '../../redux/discovery/types';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import { PeopleSearch } from '../PeopleSearch/PeopleSearch';
import { getJWtDetails, useDispatchPromise } from '../../utils/AppUtils';
import { getMyRooms, initiateMessageForRoom } from '../../redux/messages/actions';

export interface IMessageDialogueProps {
    open: boolean;
    onClose: () => void;
    recipient?: IUserSummary;
    roleId?: string;
    auditionId?: string;
    fromModal?: boolean;
    /** Called before sending. Return false to block the send (e.g. to prompt login). */
    onBeforeSend?: () => boolean;
}

const fieldLabelSx = (muted: string) => ({
    display: 'block',
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: muted,
    mb: 1,
});

const MessageDialogue: React.FC<IMessageDialogueProps> = ({
    open,
    onClose,
    recipient,
    roleId,
    auditionId,
    fromModal,
    onBeforeSend,
}) => {
    const muiTheme = useTheme();
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedRecipients, setSelectedRecipients] = useState<IUserSummary[]>([]);
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const { user_id } = getJWtDetails();
    const dispatch = useDispatchPromise();
    const isDark = themeStyle === 'dark';

    const muted = isDark ? 'rgba(255,255,255,0.55)' : theme[themeStyle].subtitleGrey;
    const fieldBg = isDark ? 'rgba(0,0,0,0.45)' : theme[themeStyle].true_white;
    const borderSubtle = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
    const chipBg = isDark ? 'rgba(38,38,38,0.95)' : theme[themeStyle].secondaryBg;
    const addBtnBg = isDark ? 'rgba(38,38,38,1)' : 'rgba(0,0,0,0.06)';

    useEffect(() => {
        if (recipient) {
            setSelectedRecipients([recipient]);
        }
    }, [recipient]);

    const handleClose = () => {
        setMessage('');
        onClose();
        setLoading(false);
    };

    const handleSubmit = () => {
        if (onBeforeSend && !onBeforeSend()) return;
        if (selectedRecipients.length > 0 && message.trim()) {
            const payload = {
                message_content: message,
                receiver: selectedRecipients.map(user => user._id),
                sender: user_id,
                from_modal: fromModal,
                ...(auditionId && { audition: auditionId }),
                ...(roleId && { role: roleId }),
            };
            setLoading(true);
            dispatch(initiateMessageForRoom(payload))
                .then(() => {
                    dispatch(getMyRooms({}));
                    handleClose();
                })
                .catch(() => {
                    handleClose();
                });
        }
    };

    const handleRecipientChange = (users: IUserSummary[] | IHost | IPartner[] | undefined) => {
        if (Array.isArray(users)) {
            setSelectedRecipients(users as IUserSummary[]);
        } else if (users) {
            const userSummary: IUserSummary = {
                _id: (users as IHost).id,
                id: (users as IHost).id,
                firstName: (users as IHost).name || '',
                lastName: '',
                userName: (users as IHost).name || '',
                profile_picture: (users as IHost).profile_picture || '',
                verified: false,
                role: 'host',
                audme_profile: '',
            };
            setSelectedRecipients([userSummary]);
        }
        setSearchDialogOpen(false);
    };

    const handleDeleteRecipient = (recipientToDelete: IUserSummary) => {
        setSelectedRecipients(prev => prev.filter(r => r._id !== recipientToDelete._id));
    };

    const isButtonEnabled = selectedRecipients.length > 0 && message.trim().length > 0;

    const sharedBackdrop = {
        sx: {
            backgroundColor: isDark ? 'rgba(0,0,0,0.72)' : 'rgba(15,15,15,0.45)',
            backdropFilter: 'blur(20px)',
        },
    };

    const sharedPaper = {
        borderRadius: 3,
        border: `1px solid ${borderSubtle}`,
        boxShadow: isDark ? '0 24px 64px rgba(0,0,0,0.85)' : '0 20px 50px rgba(0,0,0,0.14)',
        overflow: 'hidden',
        backgroundColor: isDark ? 'rgba(26,26,26,0.94)' : theme[themeStyle].deep_grey_lighter,
        maxWidth: { sm: 576 },
        width: '100%',
        height: { xs: '100dvh', sm: 'fit-content' },
        maxHeight: { xs: '90dvh', sm: '92vh' },
    };

    const sendGradient = isDark
        ? `linear-gradient(90deg, ${theme[themeStyle].primary_orange} 0%, ${theme[themeStyle].logo_color} 100%)`
        : 'linear-gradient(90deg, #ffbc55 0%, #e68411 100%)';
    const sendLabelColor = isDark ? theme[themeStyle].pure_black : '#1c0d00';

    return (
        <>
            <DialogueModal
                open={open}
                onClose={handleClose}
                buttonEnabled={false}
                headerBar
                header="Send Message"
                headerSubtitle="Direct communication with talent on the platform."
                headerTypographySx={{
                    textTransform: 'none',
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.35rem', sm: '1.5rem' },
                }}
                paperSX={sharedPaper}
                dialogueProps={{
                    fullWidth: true,
                    maxWidth: 'sm',
                    fullScreen: isMobile,
                    slotProps: {
                        backdrop: sharedBackdrop,
                    },
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 0.5 }}>
                    {/* To */}
                    <Box>
                        <Typography component="label" sx={fieldLabelSx(muted)}>
                            To
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1,
                                minHeight: 56,
                                px: 2,
                                py: 1.25,
                                borderRadius: 3,
                                border: `1px solid ${borderSubtle}`,
                                backgroundColor: fieldBg,
                            }}>
                            {selectedRecipients.map((rec, index) => (
                                <Chip
                                    key={rec._id ?? index}
                                    avatar={<Avatar src={rec.profile_picture} sx={{ width: 32, height: 32 }} />}
                                    label={`${rec.firstName} ${rec.lastName}`.trim() || rec.userName}
                                    onDelete={() => handleDeleteRecipient(rec)}
                                    deleteIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                                    sx={{
                                        backgroundColor: chipBg,
                                        color: theme[themeStyle].tag_white,
                                        border: `1px solid ${borderSubtle}`,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 13,
                                        height: 36,
                                        '& .MuiChip-deleteIcon': {
                                            color: muted,
                                            '&:hover': {
                                                color: theme[themeStyle].primary_red,
                                            },
                                        },
                                    }}
                                />
                            ))}
                            <IconButton
                                type="button"
                                onClick={() => setSearchDialogOpen(true)}
                                aria-label="Add recipients"
                                sx={{
                                    ml: 'auto',
                                    borderRadius: 2,
                                    bgcolor: addBtnBg,
                                    border: `1px solid ${borderSubtle}`,
                                    color: theme[themeStyle].primary_orange,
                                    '&:hover': {
                                        bgcolor: isDark ? 'rgba(239,129,0,0.18)' : 'rgba(251,188,85,0.2)',
                                        borderColor: theme[themeStyle].primary_orange,
                                    },
                                }}>
                                <AddIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Message */}
                    <Box>
                        <Typography component="label" htmlFor="message-dialogue-body" sx={fieldLabelSx(muted)}>
                            Message
                        </Typography>
                        <TextField
                            id="message-dialogue-body"
                            fullWidth
                            multiline
                            rows={6}
                            placeholder="Write your message here..."
                            variant="outlined"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    bgcolor: fieldBg,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: theme[themeStyle].tag_white,
                                    alignItems: 'flex-start',
                                    '& fieldset': {
                                        borderColor: borderSubtle,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDark ? 'rgba(255,159,77,0.35)' : theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].primary_orange,
                                        borderWidth: 1,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: muted,
                                    opacity: 1,
                                },
                            }}
                            InputProps={{
                                sx: { py: 1.25 },
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: muted, mt: 0.15 }} />
                        <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: muted, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            {"Messages are private and subject to the platform's Terms of Service."}
                        </Typography>
                    </Box>

                    <Box sx={{ pt: 1 }}>
                        <Button
                            fullWidth
                            type="button"
                            onClick={handleSubmit}
                            disabled={!isButtonEnabled || loading}
                            sx={{
                                py: 2,
                                px: 3,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.05rem',
                                background: sendGradient,
                                color: sendLabelColor,
                                boxShadow: isDark ? '0 8px 28px rgba(255,159,77,0.22)' : '0 6px 20px rgba(230,132,17,0.25)',
                                border: 'none',
                                gap: 1,
                                '&:hover': {
                                    background: sendGradient,
                                    filter: 'brightness(1.06)',
                                    boxShadow: isDark ? '0 10px 32px rgba(255,159,77,0.28)' : undefined,
                                },
                                '&:disabled': {
                                    opacity: 0.45,
                                    color: sendLabelColor,
                                },
                            }}
                            endIcon={
                                loading ? (
                                    <CircularProgress size={22} sx={{ color: sendLabelColor }} />
                                ) : (
                                    <SendIcon sx={{ fontSize: 22 }} />
                                )
                            }>
                            Send Message
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            height: 6,
                            width: '100%',
                            mt: 0.5,
                            background: `linear-gradient(90deg, transparent, ${theme[themeStyle].primary_orange}22, transparent)`,
                            borderRadius: 1,
                        }}
                    />
                </Box>
            </DialogueModal>

            <DialogueModal
                open={searchDialogOpen}
                onClose={() => setSearchDialogOpen(false)}
                buttonEnabled={false}
                header="Select Recipients"
                paperSX={{
                    height: { xs: '100dvh', sm: '70dvh' },
                    width: '100vw',
                    maxWidth: {
                        xs: '90dvw',
                        sm: '500px',
                    },
                    maxHeight: {
                        xs: '70%',
                        sm: '100%',
                    },
                }}
                headerStyle={{ marginBottom: 1.5 }}
                dialogueProps={{
                    fullWidth: true,
                    maxWidth: 'xs',
                    fullScreen: isMobile,
                }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                        }}>
                        <PeopleSearch
                            value={selectedRecipients}
                            onChange={handleRecipientChange}
                            placeholder="Search people by name or username..."
                            withProfileSearch
                            strict
                            hideCornerClose
                            selectionAccentColor={theme[themeStyle].true_highlight}
                        />
                    </Box>
                </Box>
            </DialogueModal>
        </>
    );
};

export { MessageDialogue };
